import React, { useEffect, useState } from 'react';

import MealDeliveryTimesEditor, {
  DEFAULT_CUTOFF_24,
  DEFAULT_TIMES_24,
} from './MealDeliveryTimesEditor';
import {
  fetchRestaurantMyeatsOffices,
  fetchRestaurantMyeatsOrgMealDeliveryTimes,
  updateRestaurantMyeatsOrgMealDeliveryTimes,
} from './restaurantApi';
import './css/AgePricingEditor.css';

const MEAL_SLOTS = ['Breakfast', 'Lunch', 'Snacks', 'Dinner'];

function rowsFromApiPayload(payload) {
  const map = payload && typeof payload === 'object' ? payload : {};
  return MEAL_SLOTS.map((mealSlot) => {
    const entry = map[mealSlot];
    const deliveryTime = String(
      entry?.time24 ?? entry?.deliveryTime ?? entry?.delivery_time ?? DEFAULT_TIMES_24[mealSlot],
    ).slice(0, 5);
    const orderCutoffTime = String(
      entry?.cutoffTime24 ??
        entry?.orderCutoffTime ??
        entry?.order_cutoff_time ??
        DEFAULT_CUTOFF_24[mealSlot],
    ).slice(0, 5);
    const enabled =
      entry?.enabled === undefined && entry?.Enabled === undefined
        ? true
        : Boolean(entry?.enabled ?? entry?.Enabled);
    return { mealSlot, enabled, deliveryTime, orderCutoffTime };
  });
}

/**
 * Work Eats office-specific meal delivery / cutoff times for a mapped organization.
 */
export default function WorkEatsOrgMealDeliveryTimesEditor({
  restaurantId,
  userId,
  disabled = false,
  mappedOffices: mappedOfficesProp = null,
}) {
  const [mappedOffices, setMappedOffices] = useState(() =>
    Array.isArray(mappedOfficesProp) ? mappedOfficesProp : [],
  );
  const [organizationId, setOrganizationId] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [rows, setRows] = useState(rowsFromApiPayload({}));
  const [source, setSource] = useState('restaurant');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (Array.isArray(mappedOfficesProp)) {
      setMappedOffices(mappedOfficesProp);
      return;
    }
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const offices = await fetchRestaurantMyeatsOffices(restaurantId);
        if (!cancelled) setMappedOffices(offices);
      } catch {
        if (!cancelled) setMappedOffices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mappedOfficesProp, restaurantId]);

  useEffect(() => {
    setRows(rowsFromApiPayload({}));
    setSource('restaurant');
    setMessage('');
    setOrganizationId('');
    setOrganizationName('');
  }, [restaurantId]);

  const loadTimes = async (orgIdOverride) => {
    const orgId = String(orgIdOverride || organizationId || '').trim();
    if (!restaurantId || !orgId) {
      setMessage('Select a mapped office to load delivery times.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const data = await fetchRestaurantMyeatsOrgMealDeliveryTimes(restaurantId, orgId);
      setRows(rowsFromApiPayload(data?.mealDeliveryTimes));
      setSource(data?.source || 'restaurant');
      const fromList = mappedOffices.find(
        (o) => String(o.organizationId || o.organization_id) === orgId,
      );
      setOrganizationName(
        String(data?.organizationName || fromList?.organizationName || '').trim(),
      );
      setMessage(
        data?.source === 'organization'
          ? 'Showing office-specific times for this organization.'
          : 'No office override yet — showing restaurant default meal times. Save to create an override.',
      );
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to load times.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const orgId = String(organizationId || '').trim();
    if (!restaurantId || !orgId || !userId) {
      setMessage('Select a mapped office and ensure you are signed in to save.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const data = await updateRestaurantMyeatsOrgMealDeliveryTimes(
        restaurantId,
        orgId,
        rows,
        userId,
      );
      setRows(rowsFromApiPayload(data?.mealDeliveryTimes));
      setSource(data?.source || 'organization');
      setMessage('Work Eats office delivery times saved.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to save times.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="age-pricing-editor meal-delivery-editor" style={{ marginTop: 16 }}>
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">Office delivery & cutoff times</label>
        <p className="age-pricing-editor__hint">
          Pick a mapped office, then set enabled meals, order cutoff, and delivery time. If unset,
          Work Eats uses the restaurant meal times above.
        </p>
      </div>
      <div className="get-restaurant-form-row flex" style={{ gap: 8, marginBottom: 10 }}>
        <div className="get-restaurant-form-group get-restaurant-form-group-wide">
          <label>Mapped office</label>
          <select
            value={organizationId}
            disabled={disabled || loading || saving || mappedOffices.length === 0}
            onChange={(e) => {
              const nextId = e.target.value;
              setOrganizationId(nextId);
              const found = mappedOffices.find(
                (o) => String(o.organizationId || o.organization_id) === String(nextId),
              );
              setOrganizationName(String(found?.organizationName || '').trim());
              if (nextId) void loadTimes(nextId);
            }}
          >
            <option value="">
              {mappedOffices.length === 0
                ? 'Map an office first (Map Offices tab)'
                : 'Select office…'}
            </option>
            {mappedOffices.map((o) => {
              const id = String(o.organizationId || o.organization_id || '');
              return (
                <option key={id} value={id}>
                  {o.organizationName || `Org ${id}`} (ID {id})
                </option>
              );
            })}
          </select>
        </div>
        <div className="get-restaurant-form-group" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || loading || saving || !organizationId}
            onClick={() => void loadTimes()}
          >
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
        <div className="get-restaurant-form-group" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn-primary"
            disabled={disabled || loading || saving || !organizationId}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save office times'}
          </button>
        </div>
      </div>
      {organizationName ? (
        <p className="age-pricing-editor__hint">Organization: {organizationName}</p>
      ) : null}
      {message ? <p className="age-pricing-editor__hint">{message}</p> : null}
      {organizationId ? <p className="age-pricing-editor__hint">Source: {source}</p> : null}
      <MealDeliveryTimesEditor
        value={rows}
        onChange={setRows}
        disabled={disabled || loading || saving || !organizationId}
        hideHeader
      />
    </div>
  );
}
